import React, { useState } from 'react';
import { Dialog, DialogHeader, DialogTitle, DialogContent } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Settings, ArrowLeft, Loader2, AlertTriangle, X, Save } from 'lucide-react';
import { StatusConfiguration } from './StatusConfiguration';
import type { DashboardProject } from '../../../types/project';
import { cn } from '../../../../../lib/utils';

interface StatusConfigurationModalProps {
  project: DashboardProject;
  selectedPipeSize: number;
  selectedPipe: any;
  isOpen: boolean;
  onClose: () => void;
  onStatusConfigSaved?: (savedAt: number) => void; // Callback to refresh weld data after save
  canEdit?: boolean;
}

export const StatusConfigurationModal: React.FC<StatusConfigurationModalProps> = ({
  project,
  selectedPipeSize,
  selectedPipe,
  isOpen,
  onClose,
  onStatusConfigSaved,
  canEdit = true
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [triggerSave, setTriggerSave] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [currentValidationErrors, setCurrentValidationErrors] = useState<any[]>([]);
  const [jobPipeInfo, setJobPipeInfo] = useState<{jobNumber: string, pipeName: string} | null>(null);
  const [hasModifications, setHasModifications] = useState(false);

  const handleSave = () => {
    // Always allow save button click - validation will be handled by StatusConfiguration component
    
    // Start saving process - validation modal will show if there are errors
    setIsSaving(true);
    setTriggerSave(true);
    // Reset trigger after a short delay
    setTimeout(() => setTriggerSave(false), 100);
  };

  const handleSaveComplete = () => {
    // This is called when the API call is actually complete
    setIsSaving(false);
    // Keep editing mode active - don't set setIsEditing(false)
    
    // Call the callback to refresh weld data
    if (onStatusConfigSaved) {
      onStatusConfigSaved(Date.now());
    }
    
    // Don't close the modal - keep it open for further editing
    // User can manually close using the X button or back button
    
    // Additional safeguard: ensure saving state is reset after a short delay
    setTimeout(() => {
      setIsSaving(false);
    }, 100);
  };

  // Handle validation failure - reset saving state
  const handleValidationFailure = () => {
    setIsSaving(false);
  };

  const handleValidationErrorsChange = (hasErrors: boolean, errors?: any[]) => {
    setHasValidationErrors(hasErrors);
    if (errors) {
      setCurrentValidationErrors(errors);
    }
  };

  const handleJobPipeInfoChange = (jobNumber: string, pipeName: string) => {
    setJobPipeInfo({ jobNumber, pipeName });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-[100vw] w-screen h-screen max-h-screen p-0 gap-0 overflow-y-auto overflow-x-hidden"
      >
          <div className="flex flex-col min-h-full">
            <DialogHeader className="flex-shrink-0 px-6 py-4 pr-14 border-b border-[#E2E5EA] bg-white flex flex-row items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  onClick={onClose}
                  variant="outline"
                  size="sm"
                  className="shrink-0 h-9 w-9 p-0 border-[#E2E5EA] hover:bg-page-bg"
                >
                  <ArrowLeft className="h-4 w-4 text-text-grey" />
                </Button>
                <div>
                  <h2 className="text-[18px] font-semibold text-blueprint-blue leading-tight">Status Configuration</h2>
                  <p className="text-[13px] text-heritage-grey mt-0.5">Project: {project.project.projectName}</p>
                </div>
                {hasValidationErrors && !isSaving && (
                  <div className="text-[12px] text-readable-red ml-2">
                    ⚠️ Validation errors detected
                  </div>
                )}
              </div>
              <Button
                onClick={handleSave}
                disabled={!canEdit || isSaving || !hasModifications}
                className="h-9 px-4 bg-hero-red hover:bg-hero-red/90 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 min-h-0 bg-page-bg">
              <StatusConfiguration
                project={project}
                selectedPipeSize={selectedPipeSize}
                selectedPipe={selectedPipe}
                isEditing={true}
                setIsEditing={setIsEditing}
                onSave={() => {}} // Empty callback since we use onSaveComplete
                onSaveComplete={handleSaveComplete}
                triggerSave={triggerSave}
                onValidationErrorsChange={handleValidationErrorsChange}
                onValidationFailure={handleValidationFailure}
                onJobPipeInfoChange={handleJobPipeInfoChange}
                onDirtyChange={setHasModifications}
                isModalOpen={isOpen}
                canEdit={canEdit}
              />
            </div>
          </div>
      </DialogContent>
    </Dialog>
  );
};

// Button component to open the modal
export const StatusConfigurationButton: React.FC<{
  project: DashboardProject;
  selectedPipeSize: number;
  selectedPipe: any;
  onOpen: () => void;
}> = ({ project, selectedPipeSize, selectedPipe, onOpen }) => {
  return (
    <Button
      onClick={onOpen}
      className="bg-red-600 hover:bg-red-700 flex items-center gap-2"
    >
      <Settings className="h-4 w-4" />
      Status Configuration
    </Button>
  );
};
